import { useEffect, useMemo, useState } from "react";
import { Globe2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  SUPPORTED_CURRENCIES,
  TIMEZONE_OPTIONS,
  type AppCurrency,
  type AppTimezone,
} from "@/i18n/config";
import { clinicsApi } from "@/lib/api";
import { cn, formatApiError, getCurrencyLabel } from "@/lib/utils";

interface CurrencySwitcherProps {
  variant?: "floating" | "panel" | "menu";
  className?: string;
}

export function CurrencySwitcher({ variant = "panel", className }: CurrencySwitcherProps) {
  const { currency, language, timezone, t } = useLanguage();
  const { user, selectedClinic, refreshClinics } = useAuth();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currencyCode, setCurrencyCode] = useState<AppCurrency>(currency);
  const [selectedTimezone, setSelectedTimezone] = useState<AppTimezone>(timezone as AppTimezone);
  const [timezoneSearch, setTimezoneSearch] = useState("");
  const isAdmin = user?.role?.toUpperCase() === "ADMIN";
  const currencyChangeAllowed = selectedClinic?.currency_change_allowed ?? selectedClinic?.currencyChangeAllowed ?? false;

  useEffect(() => {
    setCurrencyCode(currency);
    setSelectedTimezone(timezone as AppTimezone);
  }, [currency, timezone]);

  const filteredTimezones = useMemo(() => {
    const query = timezoneSearch.trim().toLocaleLowerCase(language);
    return TIMEZONE_OPTIONS.filter((option) =>
      `${t(option.label)} ${option.value}`.toLocaleLowerCase(language).includes(query),
    );
  }, [language, t, timezoneSearch]);

  const save = async () => {
    if (!selectedClinic || !isAdmin) return;
    setSaving(true);
    try {
      await clinicsApi.updateSettings(selectedClinic.id, {
        currency_code: currencyCode as "BRL" | "USD" | "EUR" | "GBP",
        timezone: selectedTimezone,
      });
      await refreshClinics();
      toast.success(t("Configuração regional salva"));
      setOpen(false);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className={cn(
            "justify-start rounded-xl text-clinic-text",
            variant === "floating" && "h-12 min-w-[148px] bg-clinic-surface/90 px-4 shadow-lg",
            variant === "panel" && "h-11 w-full border border-clinic-border bg-clinic-surface",
            variant === "menu" && "h-10 w-full px-2",
            className,
          )}
          aria-label={t("Configuração regional da clínica")}
        >
          <Globe2 className="mr-2 h-4 w-4" aria-hidden="true" />
          <span className="truncate">{currency} · {timezone}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("Configuração regional da clínica")}</DialogTitle>
          <DialogDescription>
            {isAdmin
              ? t("A moeda e o fuso são compartilhados por toda a clínica.")
              : t("Somente administradores podem alterar esta configuração.")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-2">
            <Label htmlFor="clinic-currency">{t("Moeda")}</Label>
            <Select
              value={currencyCode}
              onValueChange={(value) => setCurrencyCode(value as AppCurrency)}
              disabled={!isAdmin || !currencyChangeAllowed}
            >
              <SelectTrigger id="clinic-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SUPPORTED_CURRENCIES.map((code) => (
                  <SelectItem key={code} value={code}>{code} ({getCurrencyLabel(language, code)})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-clinic-text-muted">
              {!currencyChangeAllowed
                ? t("A moeda está bloqueada porque já existe histórico financeiro.")
                : t("Alterar a moeda não converte valores existentes.")}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="clinic-timezone-search">{t("Fuso horário")}</Label>
            <Input
              id="clinic-timezone-search"
              value={timezoneSearch}
              onChange={(event) => setTimezoneSearch(event.target.value)}
              placeholder={t("Buscar cidade ou fuso")}
              disabled={!isAdmin}
            />
            <Select
              value={selectedTimezone}
              onValueChange={(value) => setSelectedTimezone(value as AppTimezone)}
              disabled={!isAdmin}
            >
              <SelectTrigger aria-label={t("Fuso horário")}><SelectValue /></SelectTrigger>
              <SelectContent>
                {filteredTimezones.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{t(option.label)} · {option.value}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-clinic-text-muted">
              {t("O fuso define dias, horários da agenda e períodos dos indicadores; instantes salvos não são reescritos.")}
            </p>
          </div>
        </div>

        {isAdmin ? (
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t("Cancelar")}</Button>
            <Button type="button" onClick={save} disabled={saving}>{saving ? t("Salvando...") : t("Salvar")}</Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
